/**
 * QUEM ESTÁ NO COMANDO do microfone AGORA — [spec-008 T030b].
 *
 * Módulo FOLHA (não importa transporte nenhum) com uma pergunta só:
 * *"o caminho por turnos é o transporte da sessão aberta neste instante?"*.
 * Quem responde escreve aqui uma vez, na abertura (`transporte-de-voz.js`,
 * o ponto ÚNICO de seleção); quem pergunta é o próprio `porTurnos`.
 *
 * ## Por que isto precisou existir — o acoplamento MEDIDO na [T030]
 *
 * `transporte-por-turnos.js` instala DOIS `$watch` no estado reativo
 * COMPARTILHADO (`estado-da-voz.js`): `estadoDaFala` arma a escuta de
 * barge-in LOCAL, e `sessaoDeVozAtiva` liga o observador de silêncio. Os
 * dois ficam armados desde a CARGA do módulo, porque `transporte-de-voz.js`
 * instancia o `porTurnos` de imediato.
 *
 * O dev da [T030] mediu a consequência por MUTAÇÃO, não por leitura: pôr
 * `motorDeVoz.estadoDaFala = 'ia'` dentro do transporte em tempo real fez o
 * `criarEscutaDeBargeIn` do v1 ser ARMADO e o `marcarInicioDeFala` (a marca
 * POSICIONAL do v1) ser chamado — **sem uma linha sequer importando
 * `barge-in-de-voz.js`**. São dois juízes do mesmo turno (invariante N4,
 * `contracts/transporte.md`) por um objeto compartilhado. Por isso o
 * transporte em tempo real nasceu SEM escrever esses dois campos — e por
 * isso a tela, que decide o que desenhar por `sessaoDeVozAtiva`, não pintava
 * a UI de voz numa sessão em tempo real.
 *
 * A [T030b] tinha duas saídas nomeadas na task: **desarmar os dois `$watch`
 * quando o transporte ativo não for o por turnos**, ou movê-los para dentro
 * dele. Esta é a primeira — e a escolha está justificada em
 * `transporte-por-turnos.js`, no ponto em que os watchers perguntam.
 *
 * ## O default é `porTurnos`, e é isso que torna a bandeira DESLIGADA
 * byte a byte igual a hoje
 *
 * Enquanto ninguém abrir sessão, `_noComando` é `null` e
 * `porTurnosNoComando()` devolve `true` — os dois `$watch` do v1 seguem
 * exatamente o comportamento de sempre. Só uma sessão de um transporte com
 * OUTRO `nome` os silencia, e ela só existe com a bandeira ligada.
 *
 * ## Por que um módulo próprio, e não um campo em `transporte-de-voz.js`
 *
 * `transporte-de-voz.js` importa `transporte-por-turnos.js`. Se o `porTurnos`
 * fosse perguntar lá, seria CICLO — o mesmo tipo de armadilha que o registro
 * do transporte em tempo real já documenta ("nunca importe o seletor de quem
 * o seletor importa"). Folha resolve sem ciclo e sem esconder a pergunta
 * dentro do estado reativo da tela: isto **não** é estado de UI, e não entra
 * em `estado-da-voz.js` justamente para nenhum componente passar a lê-lo (o
 * contrato diz que `nome` NUNCA é lido por componente de tela).
 */
import { TRANSPORTE_POR_TURNOS } from './medicao-de-voz'

// O transporte que abriu a sessão corrente. `null` = nenhuma sessão aberta
// por transporte algum desde a carga do módulo, que é o mesmo que dizer "o
// caminho por turnos manda", porque é ele quem responde por todo o resto do
// sistema de voz (Ditar, fala progressiva, síntese).
let _noComando = null

/**
 * Declara quem abriu a sessão. Chamado ANTES do `abrir` do transporte
 * escolhido — a ordem importa: o `porTurnos` escreve `sessaoDeVozAtiva`
 * DENTRO do próprio `abrir`, e o `$watch` que reage a isso precisa já
 * enxergar o comando certo quando dispara.
 *
 * @param {Object|null} transporte o `TransporteDeVoz` que assume, ou `null`
 *   para devolver o comando ao caminho por turnos (fim de sessão).
 */
export const definirTransporteNoComando = (transporte) => {
  _noComando = transporte || null
}

/** O transporte da sessão corrente, ou `null` se nenhuma foi aberta. */
export const transporteNoComando = () => _noComando

/** O nome do transporte no comando — `porTurnos` quando não há sessão. */
export const nomeDoTransporteNoComando = () =>
  (_noComando && _noComando.nome) || TRANSPORTE_POR_TURNOS

/**
 * A pergunta que os dois `$watch` do v1 fazem antes de agir. `true` também
 * quando não há sessão nenhuma — ver "o default é `porTurnos`" no cabeçalho.
 */
export const porTurnosNoComando = () =>
  nomeDoTransporteNoComando() === TRANSPORTE_POR_TURNOS
